import { useEffect, useRef } from "react";
import { gsap, ScrollTrigger, prefersReducedMotion, isTouch, splitWords } from "../lib/motion";
import { scrollToSection } from "../lib/useSmoothScroll";
import Counter from "./ui/Counter";
import Button from "./ui/Button";
import Icon3D from "./ui/Icon3D";
import logoMark from "../assets/logo/logo-mark.webp";
import heroPhones from "../assets/images/hero-phones.webp";
import arrowCurve from "../assets/decor/arrow-curve.webp";
import waveHero from "../assets/decor/wave-hero.png";
import "./Hero.css";

const LINES = [
  { key: "private", text: "Private", cls: "hero__line--a" },
  { key: "stays", text: "Stays", cls: "hero__line--b" },
  { key: "jordanian", text: "Jordanian", cls: "hero__line--c" },
  { key: "hospitality", text: "Hospitality", cls: "hero__line--d" },
];

export default function Hero({ ready }) {
  const rootRef = useRef(null);
  const phonesRef = useRef(null);
  const arrowRef = useRef(null);
  const waveRef = useRef(null);
  const introDone = useRef(false);

  /* ---------------- intro timeline (runs once the preloader lifts) --------------- */
  useEffect(() => {
    if (!ready || introDone.current) return undefined;
    introDone.current = true;

    const root = rootRef.current;
    if (!root) return undefined;

    const ctx = gsap.context(() => {
      if (prefersReducedMotion()) {
        gsap.set("[data-hero-fade], .hero__arrow", { autoAlpha: 1, y: 0, clipPath: "none" });
        return;
      }

      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

      // Each headline word rises from behind its own mask.
      LINES.forEach((line, i) => {
        const el = root.querySelector(`.${line.cls} .hero__line-text`);
        const words = splitWords(el);
        if (!words) return;
        gsap.set(el, { autoAlpha: 1 });
        tl.fromTo(
          words,
          { yPercent: 116, rotate: 4 },
          { yPercent: 0, rotate: 0, duration: 1.25, stagger: 0.05 },
          0.05 + i * 0.11
        );
      });

      tl.fromTo(
        phonesRef.current,
        { autoAlpha: 0, yPercent: 14, scale: 0.92, rotate: -4 },
        { autoAlpha: 1, yPercent: 0, scale: 1, rotate: 0, duration: 1.6 },
        0.28
      )
        .fromTo(
          arrowRef.current,
          { clipPath: "inset(0% 100% 0% 0%)", autoAlpha: 1 },
          { clipPath: "inset(0% 0% 0% 0%)", duration: 1.7, ease: "power2.inOut" },
          0.55
        )
        .fromTo(
          "[data-hero-fade]",
          { autoAlpha: 0, y: 26 },
          { autoAlpha: 1, y: 0, duration: 1.1, stagger: 0.12 },
          0.75
        );
    }, root);

    return () => ctx.revert();
  }, [ready]);

  /* ---------------- scroll parallax + idle float --------------- */
  useEffect(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) return undefined;

    const ctx = gsap.context(() => {
      // Depth: wave drifts slowest, arrow mid, phones fastest.
      gsap.to(waveRef.current, {
        yPercent: 16,
        ease: "none",
        scrollTrigger: { trigger: root, start: "top top", end: "bottom top", scrub: 0.6 },
      });

      gsap.to(arrowRef.current, {
        yPercent: 34,
        xPercent: 4,
        ease: "none",
        scrollTrigger: { trigger: root, start: "top top", end: "bottom top", scrub: 0.6 },
      });

      gsap.to(phonesRef.current, {
        yPercent: 20,
        ease: "none",
        scrollTrigger: { trigger: root, start: "top top", end: "bottom top", scrub: 0.5 },
      });

      gsap.to(".hero__line--a, .hero__line--b", {
        xPercent: -6,
        autoAlpha: 0.25,
        ease: "none",
        scrollTrigger: { trigger: root, start: "top top", end: "bottom 40%", scrub: 0.8 },
      });

      gsap.to(".hero__line--c, .hero__line--d", {
        xPercent: 6,
        autoAlpha: 0.25,
        ease: "none",
        scrollTrigger: { trigger: root, start: "top top", end: "bottom 40%", scrub: 0.8 },
      });

      // Slow idle float so the mockup never feels pasted on.
      gsap.to(phonesRef.current, {
        y: "+=14",
        duration: 4.5,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
    }, root);

    return () => ctx.revert();
  }, []);

  /* ---------------- pointer-driven depth (fine pointers only) --------------- */
  useEffect(() => {
    const root = rootRef.current;
    if (!root || isTouch() || prefersReducedMotion()) return undefined;

    const setPhoneX = gsap.quickTo(phonesRef.current, "x", { duration: 1.1, ease: "power3.out" });
    const setPhoneR = gsap.quickTo(phonesRef.current, "rotate", { duration: 1.2, ease: "power3.out" });
    const setArrowX = gsap.quickTo(arrowRef.current, "x", { duration: 1.4, ease: "power3.out" });

    const onMove = (e) => {
      const nx = e.clientX / window.innerWidth - 0.5;
      const ny = e.clientY / window.innerHeight - 0.5;
      setPhoneX(nx * -34);
      setPhoneR(nx * 2.4 + ny * 1.2);
      setArrowX(nx * 22);
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, []);

  useEffect(() => {
    const id = setTimeout(() => ScrollTrigger.refresh(), 500);
    return () => clearTimeout(id);
  }, [ready]);

  return (
    <section className="hero" id="home" ref={rootRef}>
      <div
        className="hero__wave"
        ref={waveRef}
        style={{ backgroundImage: `url(${waveHero})` }}
        aria-hidden="true"
      />
      <div className="hero__glow" aria-hidden="true" />

      <div className="hero__container">
        <div className="hero__stage">
          <h1 className="hero__title" aria-label="Private Stays — Jordanian Hospitality">
            {LINES.map((line) => (
              <span key={line.key} className={`hero__line ${line.cls}`} aria-hidden="true">
                <span className="hero__line-text">{line.text}</span>
              </span>
            ))}
          </h1>

          <img
            ref={arrowRef}
            className="hero__arrow"
            src={arrowCurve}
            alt=""
            aria-hidden="true"
            width="1500"
            height="550"
          />

          <div className="hero__mark" data-hero-fade>
            <Icon3D name="house" fallbackSrc={logoMark} bare />
          </div>

          <div className="hero__stat" data-hero-fade>
            <Counter value={20} suffix="K" sup="+" label="Stays Trust Us." tone="light" />
          </div>

          <img
            ref={phonesRef}
            className="hero__phones"
            src={heroPhones}
            alt="The Private Stays app shown on two phones: the splash screen and the Book Your Private Farm onboarding screen."
            width="838"
            height="907"
            fetchPriority="high"
            decoding="async"
          />

          <div className="hero__scroll" data-hero-fade>
            <Button
              variant="circle"
              aria-label="Scroll to About"
              onClick={() => scrollToSection("#about")}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 4v15m0 0 6-6m-6 6-6-6"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
